import React from "react";
import { useCachedLeadgenForms } from "@/hooks/useCachedLeadgenForms";
import { SelectCell } from "./SelectCell";
import { AlertTriangle } from "lucide-react";
import type { AdSet } from "@/types/bulkAd";

interface InstantFormCellProps {
  value: string | null | undefined;
  onChange: (formId: string | null) => void;
  userId: string | undefined;
  adSet: AdSet | null;
  facebookPage: { id: string; name: string } | null;
}

export function InstantFormCell({ value, onChange, userId, adSet, facebookPage }: InstantFormCellProps) {
  // Determine if this is a lead gen ad set (supports both types)
  const isLeadGen = adSet?.optimizationGoal === "LEAD_GENERATION" || adSet?.optimizationGoal === "QUALITY_LEAD";

  // Get page_id from the selected Facebook Page
  const pageId = facebookPage?.id;

  // Always call the hook at the top level (React rules of hooks)
  const { data: forms = [], isLoading } = useCachedLeadgenForms(
    isLeadGen ? userId : undefined,
    isLeadGen ? pageId : undefined,
  );

  // If no ad set selected, show placeholder
  if (!adSet) {
    return <div className="px-2 py-1 text-xs text-muted-foreground/70 italic">Select Ad Set first</div>;
  }

  // If not a lead gen ad set, show N/A
  if (!isLeadGen) {
    return <div className="px-2 py-1 text-xs text-muted-foreground/50 italic">N/A</div>;
  }

  // If lead gen but no Facebook Page selected, show warning
  if (!pageId) {
    return (
      <div className="flex items-center gap-1 px-2 py-1 text-xs text-warning">
        <AlertTriangle className="h-3 w-3" />
        <span>Select FB Page first</span>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-1 text-muted-foreground">
        <span className="text-xs">Loading...</span>
      </div>
    );
  }

  // Map forms to dropdown options using display_name
  const options = forms.map((form) => ({
    id: form.form_id,
    name: form.display_name || `${form.form_name} (${form.form_id})`,
  }));

  const handleChange = (selectedValue: string) => {
    if (!selectedValue) {
      onChange(null);
      return;
    }
    // Store the form_id directly
    onChange(selectedValue);
  };

  // Check if form is required but not selected (for visual indication)
  const isRequired = isLeadGen && !value;

  return (
    <div className={isRequired ? "ring-1 ring-destructive/50 rounded" : ""}>
      <SelectCell value={value || ""} onChange={handleChange} options={options} placeholder="Select Form..." />
    </div>
  );
}
